<template>
    <loading v-if="loading" :text="$t('find.findingYourTrip')"/>
    <h2 v-else-if="error" class="text-white text-center my-5">{{ $t('trip.errors.getTrip.default') }}</h2>
    <div v-else>
        <!--TODO abstract this to a component. Requires careful coordination with the header in Payment.vue until we do that.-->
        <div class="d-flex flex-wrap align-items-center mt-4 mx-1">
            <h2 class="text-white flex-grow-1 mb-4 me-2 text-nowrap">{{$t('trip.pageTitle')}}</h2>
            <button
                v-if="paymentAllowed"
                @click="redirectToPayment"
                type="button"
                class="btn btn-sm btn-primary my-3 text-nowrap"
            >
                {{$t('trip.payForThisTrip')}}
            </button>
        </div>
        <div v-if="actionCompleted === 'PAYMENT_COMPLETED'" class="card mb-5">
            <!--TODO add dismiss button to this message -->
            <!--TODO smaller text on mobile -->
            <div class="card-body text-center py-4">
                <font-awesome-icon :icon="['fal', 'shield-check']" class="fa-5x text-primary mb-3"/>
                <h3 class="text-dark mb-3">{{$t('trip.securePaymentReceived')}}</h3>
                <div class="lead mb-3">{{$t('trip.travelAgentContact')}}</div>
                <div>{{$t('trip.thankYouForUsing')}}</div>
            </div>
        </div>
        <div v-else-if="actionCompleted === 'PAYMENT_BLOCKED'" class="card mb-5">
            <!--TODO add dismiss button to this message -->
            <!--TODO smaller text on mobile -->
            <div class="card-body text-center py-4">
                <font-awesome-layers class="fa-7x mb-3 text-danger">
                    <font-awesome-icon :icon="['fal', 'credit-card']" fixed-width transform="shrink-7" class="text-dark" />
                    <font-awesome-icon :icon="['fal', 'ban']" fixed-width />
                </font-awesome-layers>
                <h3 class="text-dark">{{$t('trip.errors.unableToProcess')}}</h3>
                <div class="lead">{{$t('trip.contactTravelAdvisor')}}</div>
            </div>
        </div>
        <div v-else-if="loadingWarning !== false" class="card mb-5">
            <!--TODO add dismiss button to this message -->
            <!--TODO smaller text on mobile -->
            <div class="card-body text-center py-4">
                <font-awesome-icon v-if="loadingWarning === 'linkCompleted'" :icon="['fal', 'link']" class="fa-5x text-primary mb-3"/>
                <font-awesome-icon v-if="loadingWarning === 'linkExpired'" :icon="['fal', 'link-slash']" class="fa-5x text-danger mb-3"/>
                <h4 class="text-dark">{{ this.$t(`payment.errors.getTripAndLink.${loadingWarning}`) }}</h4>
            </div>
        </div>
        <div v-else-if="tripType !== 'GROUP' && this.trip.brand.clientEnabled && trip.clientStatus === 'CONFIRMED' && !trip.individual.activeCardholderPaymentInFullLink" class="card mb-5">
            <!--TODO add dismiss button to this message -->
            <!--TODO smaller text on mobile -->
            <div class="card-body text-center py-4">
                <font-awesome-icon :icon="['fal', 'hexagon-exclamation']" class="fa-5x text-danger mb-3"/>
                <h4 class="text-dark">{{$t('trip.errors.needsPaymentNoLink')}}</h4>
                <div class="lead">{{$t('trip.contactTravelAdvisor')}}</div>
            </div>
        </div>

        <div class="row gx-2 gx-sm-3 align-items-stretch">
            <div class="col-xl-8 mb-5 d-flex">
                <trip-summary :trip="trip"/>
            </div>
            <div class="col-xl-4 mb-5 d-flex">
                <managed-trip-user v-if="tripType === 'GROUP' || !trip.agent" :user="trip.user"/>
                <travel-advisor v-else :agent="trip.agent"/>
            </div>
        </div>

        <div v-if="tripType === 'INDIVIDUAL'">
            <travelers
                v-if="trip.pnr.travelers"
                :trip="trip"
                :show-pricing="paymentAllowed && this.trip.pnr.totalPrice !== undefined"
                class="mb-5"
            />

            <itinerary :trip="trip" class="mb-5"/>

            <div v-if="paymentAllowed">
                <!--TODO use <a> tag / router-link component instead of @click? -->
                <button @click="redirectToPayment" type="button" class="btn btn-lg btn-primary w-100 btn-payment mb-5">
                    {{$t('trip.payForThisTrip')}}
                </button>
                <div class="d-flex align-items-center justify-content-center">
                    <font-awesome-icon :icon="['fal', 'hourglass']" class="fa-2x text-white me-3"/>
                    <h3 class="text-white mb-0">{{$t('trip.payBy', {time: $filters.beautifyDateTime(trip.finalPaymentDueCstDateTime)})}}</h3>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
//External Components
import { FontAwesomeLayers } from '@fortawesome/vue-fontawesome'

//P4MT Components
import Itinerary from '@/components/itinerary/Itinerary';
import ManagedTripUser from "@/components/ManagedTripUser";
import TravelAdvisor from '@/components/TravelAdvisor';
import TripSummary from '@/components/TripSummary';
import Travelers from '@/components/Travelers';
import Loading from '@/components/common/Loading';
import {getTrip} from '@/api';

export default {
    name: 'Trip',
    components: {
        FontAwesomeLayers,
        Itinerary,
        ManagedTripUser,
        TripSummary,
        TravelAdvisor,
        Travelers,
        Loading
    },
    data() {
        return {
            loading: true,
            loadingWarning: false,
            error: false,
            actionCompleted: null
        }
    },
    computed: {
        trip() {
            return this.$store.state.trip;
        },
        tripType() {
            if(this.trip === null) return null;

            return this.trip.group || !this.trip.pnr ? 'GROUP' : 'INDIVIDUAL';
        },
        paymentAllowed() {
            //Helper that makes sure we have an active Payment-in-full link and client is allowed to pay.
            //(clientActions takes into account things like final payment due & payment block reviews)
            return (
                this.tripType === 'INDIVIDUAL' &&
                this.trip.individual.activeCardholderPaymentInFullLink !== null &&
                this.trip.pnr.clientActions.includes('APPLY_PAYMENT')
            );
        }
    },
    methods: {
        redirectToPayment() {
            this.$router.push({
                name: 'payment',
                params: {
                    tripId: this.trip.id,
                    linkId: this.trip.individual.activeCardholderPaymentInFullLink.id,
                    linkTinyHash: this.trip.individual.activeCardholderPaymentInFullLink.tinyHash
                }
            });
        }
    },
    mounted() {
        //If there is a completed action in the store, pull it into our local data and clear it out in store.
        if (this.$store.state.actionCompleted !== null) {
            this.actionCompleted = this.$store.state.actionCompleted;
            this.$store.commit('setActionCompleted', null);
        }

        //If we are missing the trip, or we have just completed an action, go get fresh copy of the trip from API.
        if(this.trip === null || this.actionCompleted !== null) {
            this.loading = true;
            getTrip(this.$route.params.tripId, this.$route.params.tripTinyHash, this.$route.params.targetLinkId)
                .then(response => {
                    this.$store.commit('setTrip', response.data.trip);

                    //Look in the error response for "known" errors that we want to pass onto the user.
                    if(!this.paymentAllowed && response.data.warnings) {
                        let knownWarnings = ['linkCompleted', 'linkExpired'];
                        knownWarnings.forEach(knownWarning => {
                            if(response.data.warnings[knownWarning]) {
                                this.loadingWarning = knownWarning;
                            }
                        })
                    }
                })
                .catch(() => {
                    //TODO redirect to nice error page.
                    this.error = true;
                })
                .finally(() => {
                    this.loading = false;
                });
        } else {
            //Trip is already present.
            this.loading = false;
        }
    }
}
</script>

<style lang="scss" scoped>
    .btn-payment {
        padding: 1.25rem 0;
        font-size: 1.5rem;
        line-height: 1.75rem;
        small {
            font-size: 0.75rem;
            text-transform: none;
        }
    }
</style>